import type { ProjectDetail } from "@esi/shared";
import { FileText, ScrollText } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { formatAgo, formatNumber, formatPercent } from "../../i18n/format.ts";
import { projectHomePath, wikiPath } from "../../router.tsx";
import { useFigures } from "./Figures.tsx";
import { FigureLine, HomeRow, HomeRows, HomeSection } from "./HomeSection.tsx";

/**
 * Usage (spec `project-home` "Usage for managers", design D8), for `project.view_activity`
 * (managers and owners): questions, active members and the share of answers with sources over
 * 30 days, then the most cited documents the viewer can read, as counts with the rollup's
 * freshness. The server omits it below five active members, and the section is then omitted
 * too, as it is while the summary loads or fails (nothing to show in its place). It names no
 * person and never says a cited document was left out.
 */
export function UsageSection({ project }: { project: ProjectDetail }) {
  const { t } = useTranslation();
  const citedId = useId();
  const { summary } = useFigures(project.id);
  const usage = summary.data?.usage;
  if (!usage) {
    return null;
  }
  // Before the first rollup the figures are not counted yet: a dash for each and no citation
  // counts, never a number beside "not computed yet".
  const computed = usage.rolledUpAt !== null;
  const share = usage.sourcedShare;
  const figure = (value: string) => (computed ? value : "–");
  return (
    <HomeSection
      id="usage"
      title={t("projectOverview.usage.title")}
      summaryLine={
        computed
          ? [
              t("projectOverview.usage.questionsCount", { count: usage.questions }),
              t("projectOverview.usage.activeCount", { count: usage.activePeople }),
            ].join(" · ")
          : t("projectOverview.usage.notComputedShort")
      }
      foot={
        usage.rolledUpAt !== null
          ? t("projectOverview.usage.updated", { when: formatAgo(usage.rolledUpAt) })
          : t("projectOverview.usage.notComputed")
      }
    >
      <ul className="divide-y divide-line">
        <FigureLine
          label={t("projectOverview.usage.questions")}
          value={figure(formatNumber(usage.questions))}
        />
        <FigureLine
          label={t("projectOverview.usage.active")}
          value={figure(formatNumber(usage.activePeople))}
        />
        <FigureLine
          label={t("projectOverview.usage.sourced")}
          value={share === null ? "–" : figure(formatPercent(share))}
        />
      </ul>
      {computed && usage.topCited.length > 0 ? (
        <div className="border-t border-line">
          <p id={citedId} className="px-4 pt-2.5 text-xs font-medium text-fg-muted">
            {t("projectOverview.usage.topCited")}
          </p>
          <HomeRows labelledBy={citedId}>
            {usage.topCited.map((cited) => (
              <HomeRow
                key={cited.id}
                icon={cited.kind === "wiki" ? ScrollText : FileText}
                href={
                  cited.kind === "wiki"
                    ? wikiPath(project.id, cited.id)
                    : `${projectHomePath(project.id, "files")}?document=${encodeURIComponent(cited.id)}`
                }
                title={cited.title}
                meta={[t("projectOverview.usage.citations", { count: cited.citations })]}
              />
            ))}
          </HomeRows>
        </div>
      ) : null}
    </HomeSection>
  );
}
